import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import Skeleton from 'react-loading-skeleton';
import styles from '@components/ProductCardNew/ProductImage/ProductImage.styles';
import { IProductPackshots } from '@features/categories/typescript';
import { NUM_OF_PACKSHOTS, PRODUCT_CARD_THUMBNAIL_SIZE } from '@features/categories/const';
import PackshotImage from '@components/ProductCardNew/ProductImage/PackshotImage/PackshotImage';
import PackshotVideo from '@components/ProductCardNew/ProductImage/PackshotVideo/PackshotVideo';
import PackshotImageHover from '@components/ProductCardNew/ProductImage/PackshotImageHover/PackshotImageHover';
type PropsType = {
  packshots: IProductPackshots;
  alt: string;
  setIsLoading: (isLoading) => void;
  isSmall: boolean;
  isSlider: boolean;
  isSearch?: boolean;
};
const ProductImage = ({
  packshots,
  alt,
  setIsLoading = () => null,
  isSmall,
  isSlider,
  isSearch = false
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const imageAlt = alt?.trim() ? alt : t('productImageNotAvailable');
  const [standardLoaded, setStandardLoaded] = useState(false);
  const [showPlaceholder, setPlaceholder] = useState(false);
  const {
    main: mainPackshots,
    hover: hoverPackshots,
    mainShadow,
    mainVideo,
    hoverVideo,
    hoverOpened
  } = packshots;
  const onLoadFunc = useCallback(() => {
    setStandardLoaded(true);
    setIsLoading(false);
  }, [setIsLoading]);
  const onErrorFunc = useCallback(() => {
    setStandardLoaded(false);
    setPlaceholder(true);
    setIsLoading(false);
  }, [setIsLoading]);
  const getPoster = useCallback(() => {
    if (mainShadow.length) {
      return mainShadow[0].url;
    }
    if (mainPackshots.length) {
      return mainPackshots[0].url;
    }
    return '';
  }, [mainShadow, mainPackshots]);
  const renderMainImage = useMemo(() => {
    if (mainVideo.length) {
      return mainVideo.slice(0, NUM_OF_PACKSHOTS).map(video => <PackshotVideo key={video.code} url={video.url} poster={getPoster()} onLoadFunc={onLoadFunc} onErrorFunc={onErrorFunc} alt={imageAlt} />);
    }
    if (mainShadow.length) {
      return mainShadow.slice(0, NUM_OF_PACKSHOTS).map(packshot => <PackshotImage key={packshot.code} url={packshot.url} thumbnailFormats={packshot.thumbnails?.[PRODUCT_CARD_THUMBNAIL_SIZE]} alt={imageAlt} showPlaceholder={showPlaceholder} onLoadFunc={onLoadFunc} onErrorFunc={onErrorFunc} standardLoaded={standardLoaded} isBig isSearch={isSearch} />);
    }
    if (mainPackshots.length) {
      return mainPackshots.slice(0, NUM_OF_PACKSHOTS).map(packshot => <PackshotImage key={packshot.code} url={packshot.url} thumbnailFormats={packshot.thumbnails?.[PRODUCT_CARD_THUMBNAIL_SIZE]} alt={imageAlt} onLoadFunc={onLoadFunc} onErrorFunc={onErrorFunc} showPlaceholder={showPlaceholder} isSlider={isSlider} isSmall={isSmall} standardLoaded={standardLoaded} isSearch={isSearch} />);
    }
    return <PackshotImage url="" alt={imageAlt} onLoadFunc={onLoadFunc} onErrorFunc={onErrorFunc} showPlaceholder isSlider={isSlider} isSmall={isSmall} standardLoaded={false} isSearch={isSearch} />;
  }, [mainPackshots, mainVideo, mainShadow, standardLoaded, showPlaceholder, imageAlt, getPoster, onLoadFunc, onErrorFunc, isSlider, isSmall, isSearch]);
  const renderHover = useMemo(() => {
    if (hoverVideo.length) {
      return hoverVideo.slice(0, NUM_OF_PACKSHOTS).map(video => <PackshotVideo key={video.code} url={video.url} isHover alt={imageAlt} />);
    }
    if (hoverPackshots.length) {
      return hoverPackshots.slice(0, NUM_OF_PACKSHOTS).map(hover => <PackshotImageHover key={hover.code} alt={`${imageAlt} ${t('arrangement.single')}`} url={hover.url} thumbnailFormats={hover.thumbnails?.[PRODUCT_CARD_THUMBNAIL_SIZE]} />);
    }
    if (hoverOpened.length) {
      return hoverOpened.slice(0, NUM_OF_PACKSHOTS).map(hover => <PackshotImageHover key={hover.code} alt={`${imageAlt} ${t('arrangement.single')}`} url={hover.url} thumbnailFormats={hover.thumbnails?.[PRODUCT_CARD_THUMBNAIL_SIZE]} isOpened isSmall={isSmall} isSlider={isSlider} />);
    }
    return null;
  }, [hoverOpened, hoverPackshots, hoverVideo, imageAlt, t, isSmall, isSlider]);
  return <>
      {!standardLoaded && !showPlaceholder && <Skeleton count={1} sx={styles.mainImageSkeleton} enableAnimation />}
      <div sx={styles.visualContainer}>
        {renderMainImage}
        {renderHover}
      </div>
    </>;
};
export default ProductImage;